import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { formatMoney, toString } from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import { ZERO_AMOUNT, parseAmount } from '@aeris/sales'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  InlineAlert,
  Select,
  Spinner,
} from '@aeris/ui'

import { fetchCustomerCredit } from '../lib/api/exchanges.ts'
import {
  ApiError,
  depositSalesDocument,
  fetchDepositOptions,
  fetchSalesDocument,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { customerLookup } from '../lib/customer-lookup.ts'

import { PosPaymentDialog } from './pos-payment-dialog.tsx'

/**
 * Receber o sinal de um pedido confirmado (F173).
 *
 * O recebimento do balcão, em modo sinal: o teto é o que falta pagar, o campo
 * começa vazio, e só aparecem as formas que servem de sinal (nenhuma que gere
 * recebível). O dinheiro entra num caixa aberto da filial — o vendedor não
 * opera caixa, mas o sinal que ele lança entra num deles; com mais de um
 * aberto, ele escolhe qual. Na loja que não trabalha com caixa (F174), não há
 * o que escolher: o sinal é gravado direto.
 */
export const OrderDepositDialog = ({
  orderId,
  customerPartyId,
  onClose,
  onDone,
}: {
  orderId: string
  customerPartyId: string
  onClose: () => void
  onDone: (message: string) => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const [tillId, setTillId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['sales-document', orderId],
    queryFn: async ({ signal }) => await fetchSalesDocument(orderId, signal),
  })
  const options = useQuery({
    queryKey: ['deposit-options'],
    queryFn: async ({ signal }) => await fetchDepositOptions(signal),
  })
  const voucher = useQuery({
    queryKey: ['customer-credit', customerPartyId],
    queryFn: async ({ signal }) =>
      await fetchCustomerCredit(customerPartyId, signal),
    enabled: allows('exchange.read'),
  })

  const tills = options.data?.tills ?? []
  const chosenTill =
    tillId ?? (tills.length === 1 ? (tills[0]?.id ?? null) : null)
  /*
   * A loja sem caixa (F174) não escolhe caixa nenhum: o sinal é gravado
   * direto. Enquanto a resposta não chega, supõe que tem — é o que recusa.
   */
  const usesTill = options.data?.usesTill ?? true

  const save = useMutation({
    mutationFn: async (
      payments: readonly { paymentMethodId: string; amount: string }[]
    ) =>
      await depositSalesDocument(orderId, {
        cashSessionId: usesTill ? chosenTill : null,
        payments,
      }),
    onSuccess: async (response) => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
      await queryClient.invalidateQueries({
        queryKey: ['sales-document', orderId],
      })
      await queryClient.invalidateQueries({ queryKey: ['production-queue'] })
      await queryClient.invalidateQueries({
        queryKey: ['production-order', orderId],
      })
      onDone(
        `Sinal recebido no pedido nº ${formatDocumentNumber(response.document.number, '')}. Falta pagar ${formatMoney(parseAmount(response.document.outstandingAmount) ?? ZERO_AMOUNT)}.`
      )
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível receber o sinal.'
      )
    },
  })

  const loading = detail.isPending || options.isPending
  const outstanding = parseAmount(detail.data?.document.outstandingAmount ?? '')

  /*
   * Antes do recebimento: carregar, avisar que não há caixa aberto, ou
   * perguntar qual caixa quando há mais de um.
   */
  if (loading || (usesTill && chosenTill === null) || outstanding === null) {
    return (
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) onClose()
        }}
      >
        <DialogContent title="Receber sinal">
          {loading ? <Spinner label="Carregando o pedido" /> : null}
          {detail.isError || options.isError ? (
            <InlineAlert tone="danger">
              Não foi possível carregar o pedido.
            </InlineAlert>
          ) : null}
          {!loading && usesTill && tills.length === 0 ? (
            <InlineAlert tone="warning">
              Nenhum caixa aberto nesta filial. O sinal entra no caixa do dia:
              peça a quem opera o caixa para abrir antes.
            </InlineAlert>
          ) : null}
          {!loading && usesTill && tills.length > 1 ? (
            <label className="flex flex-col gap-1 text-(length:--text-sm)">
              Em qual caixa o sinal entra?
              <Select
                value={tillId ?? ''}
                onChange={(event) => {
                  setTillId(
                    event.target.value === '' ? null : event.target.value
                  )
                }}
              >
                <option value="">Escolha o caixa</option>
                {tills.map((till) => (
                  <option key={till.id} value={till.id}>
                    {till.terminalId}
                    {till.openedByName === null
                      ? ''
                      : ` — ${till.openedByName}`}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <PosPaymentDialog
      deposit
      total={outstanding}
      methods={options.data?.methods ?? []}
      credit={null}
      voucherBalance={voucher.data?.balance ?? null}
      voucherLookup={customerLookup(voucher, {
        hasCustomer: true,
        allowed: allows('exchange.read'),
      })}
      pending={save.isPending}
      error={error}
      onConfirm={(tenders) => {
        save.mutate(
          tenders.map((tender) => ({
            paymentMethodId: tender.methodId,
            amount: toString(tender.amount),
          }))
        )
      }}
      onClose={onClose}
    />
  )
}
